import React, { useState, useEffect } from "react";
import { Modal, Button, Image, Container } from "react-bootstrap";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { BsFileEarmarkPdfFill, BsEye } from "react-icons/bs";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import ImageZoomPreviewer from "./ImageZoomPreviewer";
import { getFile } from "../utils/getFile";
import config from "../config";
import { FaFileCircleQuestion } from "react-icons/fa6";

const FilePreviewer = ({ fileSource }) => {
  const [show, setShow] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  const isFile = fileSource instanceof File;

  useEffect(() => {
    let objectUrl = null;

    const loadFile = async () => {
      if (!fileSource) return;

      if (isFile) {
        objectUrl = URL.createObjectURL(fileSource);
        setPreviewUrl(objectUrl);
        setFileType(fileSource.type);

        if (fileSource.type === "application/pdf") {
          const reader = new FileReader();
          reader.onloadend = (e) => {
            setPdfDataUrl(e.target.result);
          };
          reader.readAsDataURL(fileSource);
        }
      } else if (typeof fileSource === "string") {
        const ext = fileSource.split(".").pop().toLowerCase();
        const guessedType =
          ext === "pdf"
            ? "application/pdf"
            : ["jpg", "jpeg", "png", "gif", "bmp", "webp", "avif"].includes(ext)
            ? "image"
            : null;

        setFileType(guessedType);

        const fetchedBlobUrl = await getFile(
          `${config?.IMAGE_URL}${fileSource}`
        );
        if (fetchedBlobUrl) {
          objectUrl = fetchedBlobUrl;
          setPreviewUrl(fetchedBlobUrl);
          if (guessedType === "application/pdf") {
            setPdfDataUrl(fetchedBlobUrl);
          }
        }
      }
    };

    loadFile();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileSource]);

  if (!fileSource || !previewUrl || !fileType)
    return (
      <div className="text-center">
        <FaFileCircleQuestion
          title="File not found"
          className="text-warning"
          size={55}
        />
        <p>File not found !</p>
      </div>
    );

  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  const isImage = fileType.startsWith("image");
  const isPdf = fileType === "application/pdf";

  return (
    <div className="text-center mb-3">
      {isImage && (
        <>
          <Image src={previewUrl} thumbnail style={{ maxHeight: "150px" }} />
          <div className="mt-2">
            <Button variant="outline-primary" size="sm" onClick={handleOpen}>
              <BsEye className="me-1" />
              View Image
            </Button>
          </div>
        </>
      )}

      {isPdf && (
        <>
          <BsFileEarmarkPdfFill size={60} color="#dc3545" className="mb-2" />
          <div className="text-muted small mb-1">
            {isFile ? fileSource.name : fileSource}
          </div>
          <Button variant="outline-primary" size="sm" onClick={handleOpen}>
            <BsEye className="me-1" />
            View PDF
          </Button>
        </>
      )}

      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{isImage ? "Image Preview" : "PDF Preview"}</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: 0 }}>
          <Container fluid style={{ padding: "1rem" }}>
            {isImage ? (
              <>
                <Image src={previewUrl} fluid />
                {isFile && (
                  <ImageZoomPreviewer
                    file={fileSource}
                    show={show}
                    onClose={() => setShow(false)}
                  />
                )}
              </>
            ) : (
              <>
                <div className="d-flex gap-2 mb-3">
                  <ZoomInButton />
                  <ZoomOutButton />
                  <ZoomPopover />
                </div>
                <div style={{ height: "500px", width: "100%" }}>
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer
                      fileUrl={pdfDataUrl}
                      plugins={[zoomPluginInstance]}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </Worker>
                </div>
              </>
            )}
          </Container>
        </Modal.Body>

        <Modal.Footer style={{ padding: "1rem" }}>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FilePreviewer;
